import { ColumnsType } from "antd/es/table";
import { ApiParams } from "@lib/types/Api";
import { NavigateFunction } from "react-router-dom";
import { MessageFormatElement } from "react-intl";
import { ColumnType } from "antd/lib/table";
import StringOrNumberSearchFilter from "@lib/components/MiniComponents/TableFilters/StringOrNumberSearchFilter";
import DropdownSearchFilter from "@lib/components/MiniComponents/TableFilters/DropdownSearchFilter";
import DateRangeSearchFilter from "@lib/components/MiniComponents/TableFilters/DateRangeSearchFilter";
import * as yup from "yup";
import { RuleObject } from "antd/es/form";
import { AppContext, AuthRoles } from "@lib/types";
import dayjs from "dayjs";
import { Tooltip, Typography } from "antd";
import { FilterDropdownProps } from "antd/es/table/interface";
import NumberRangeFilter from "@lib/components/MiniComponents/TableFilters/NumberRangeFilter";
import _ from "lodash";
import TableApiFilters from "@lib/components/MiniComponents/TableApiFilters";
import React from "react";

const dateFields = ["createdAt", "updatedAt", "dueDate", "confirmedDate"];
const dateByFields = ["createdBy", "updatedBy"];
const actionFields = ["actions"];

export type ColumnConfig<T = Record<string, any>> = {
  apiData?: T[];
  title: string;
  dataIndex: keyof T | string;
  key?: string;
  width?: string | number;
  fixed?: "left" | "right";
  align?: "left" | "right";
  filterType?: "custom" | "search" | "dropdown" | "dateRange" | "numberRange";
  filterTitle?: string;
  customFilter?: (props: FilterDropdownProps) => React.ReactNode;
  filterOptions?: { text?: any; value?: any; label?: string }[];
  sorter?: boolean;
  render?: (value: any, record: T) => JSX.Element | string;
  className?: string;
  filters?: { text: any; value: any }[];
  dontSort?: boolean;
  dontFilter?: boolean;
  hidden?: boolean;
  hiddenFor?: AppContext[];
  hiddenByRole?: AuthRoles[];
} & (
  | { hiddenByRole: AuthRoles[]; userRole: AuthRoles }
  | { hiddenByRole?: never; userRole?: never }
);

export type ColumnConfigOverride<T> = Partial<ColumnConfig<T>>;

type GenerateColumnsProps<T> = {
  apiData?: T[];
  params?: ApiParams;
  setParams?: (params: Partial<ApiParams>) => void;
  setFilter?: (filter: string) => void;
  messages: Record<string, string> | Record<string, MessageFormatElement[]>;
  navigate?: NavigateFunction;
  [key: string]: any;
};

export const generateColumns = <T extends Record<string, any>>(
  props: GenerateColumnsProps<T>,
  columnsConfig: ColumnConfig<T>[],
): ColumnsType<T> => {
  const { apiData, params, messages, context, userRole } = props;
  const overrideColumnsConfig = props.columnsConfig;

  const filters = (params?.filter || []).map((f) => {
    const [key, type, value] = f.split("||");
    return { key, type, value };
  });

  const filteredColumnsConfig = columnsConfig
    .filter(
      (col) =>
        !overrideColumnsConfig?.hiddenColumns?.includes(
          col.dataIndex as string,
        ),
    )
    .filter(
      (col) =>
        !overrideColumnsConfig?.noAction ||
        !actionFields.includes(col.dataIndex as string),
    )
    .map((col) => {
      const hidden =
        col.hidden ||
        col.hiddenFor?.includes(context) ||
        col.hiddenByRole?.includes(userRole as AuthRoles) ||
        (col.hiddenByRole && !userRole);

      const isDateField = dateFields.includes(col.dataIndex as string);
      const isDateByField = dateByFields.includes(col.dataIndex as string);

      const noFilter = overrideColumnsConfig?.noFilter || col?.dontFilter;
      const noSort = overrideColumnsConfig?.noSort || col?.dontSort;

      const baseColumn: ColumnType<T> = {
        ...col,
        title: (
          <Typography.Text ellipsis>
            {(messages[col.title] as string) || col.title}
          </Typography.Text>
        ),
        dataIndex: col.dataIndex as any,
        key: col.dataIndex as any,
        width: col.width,
        fixed: col.fixed,
        align: col.align,
        render: col.render,
        className: col.className,
        filters: noFilter ? undefined : col.filters,
        hidden: hidden,
      };

      if (isDateField) {
        if (!baseColumn?.width) baseColumn.width = "150px";

        if (!noFilter) {
          if (!col?.filterType) {
            col.filterType = "dateRange";
          }
        }

        if (!baseColumn?.render) {
          baseColumn.render = (d) => (
            <Tooltip title={dayjs(d).format("YYYY-MM-DD HH:mm:ss")}>
              {dayjs(d).fromNow()}
            </Tooltip>
          );
        }
      }

      if (isDateByField) {
        if (!baseColumn?.width) baseColumn.width = "150px";

        if (!noFilter) {
          if (!col?.filterType) {
            col.filterType = "dropdown";
          }
        }
      }

      if (!noFilter) {
        // Check if dataIndex has a nested structure (e.g., "customer.name")
        // and if there's an API filter component registered in TableApiFilters
        const dataIndexStr = (col?.key || col?.dataIndex) as string;

        const ApiFilterComponent =
          TableApiFilters.find((f) => f.key.includes(dataIndexStr))
            ?.component || null;

        // Use API filter component if available and filterType is not explicitly set
        if (ApiFilterComponent && !col.filterType) {
          baseColumn.filterDropdown = (props) => (
            <ApiFilterComponent
              {...props}
              dataIndex={col?.dataIndex}
              apiData={apiData}
            />
          );
        } else if (col.filterType === "search") {
          baseColumn.filterDropdown = ({ confirm, setSelectedKeys }) => (
            <StringOrNumberSearchFilter
              label={(messages[col.title] as string) || col.title}
              field={col.dataIndex as string}
              value={filters.find((f) => f.key === col.dataIndex)?.value}
              onFinish={(e) => {
                setSelectedKeys(e.value ? (`%${e.value}%` as any) : "");
                confirm();
              }}
            />
          );
        } else if (col.filterType === "dropdown") {
          const dataIndexKeys = (col.dataIndex as string).split(".");
          const uniqueValues = [
            ...new Set(
              apiData?.map((d) =>
                dataIndexKeys?.reduce((acc, key) => acc?.[key], d),
              ),
            ),
          ];

          baseColumn.filterDropdown = ({ confirm, setSelectedKeys }) => (
            <DropdownSearchFilter
              field={col.dataIndex as string}
              value={filters
                .find((f) => f.key === col.dataIndex)
                ?.value?.split(",")}
              data={
                col?.filters ||
                uniqueValues.map((val) => ({
                  text: val,
                  value: val,
                }))
              }
              onFinish={(e) => {
                setSelectedKeys(e.value);
                confirm();
              }}
            />
          );

          // baseColumn.onFilter = (value, record) => record[col.dataIndex] === value; // no need
        } else if (col.filterType === "dateRange") {
          baseColumn.filterDropdown = ({ confirm, setSelectedKeys }) => (
            <DateRangeSearchFilter
              field={col.dataIndex as string}
              value={
                filters
                  .find((f) => f.key === col.dataIndex)
                  ?.value?.split(",") || []
              }
              onFinish={(e) => {
                setSelectedKeys(e.value);
                confirm();
              }}
            />
          );
        } else if (col.filterType === "numberRange") {
          baseColumn.filterDropdown = ({ confirm, setSelectedKeys }) => (
            <NumberRangeFilter
              field={col.dataIndex as string}
              value={
                filters
                  .find((f) => f.key === col.dataIndex)
                  ?.value?.split(",") || []
              }
              onFinish={(e) => {
                setSelectedKeys(e.value);
                confirm();
              }}
              filterOptions={col?.filterOptions || []}
              title={col?.filterTitle || col?.title}
            />
          );
        } else if (col.filterType === "custom") {
          baseColumn.filterDropdown = col.customFilter;
        }
      }

      if (noFilter) {
        col.filterType = undefined;
        col.filters = undefined;
        baseColumn.filterDropdown = undefined;
        baseColumn.filterSearch = undefined;
        baseColumn.onFilter = undefined;
        baseColumn.filters = undefined;
      }

      if (!noSort && col.dataIndex !== "actions") {
        const dataType = typeof apiData?.[0]?.[col?.dataIndex];

        if (dataType == "number") {
          baseColumn.sorter = (a, b) =>
            (a[col.dataIndex] || 0) - (b[col.dataIndex] || 0);
        } else {
          baseColumn.sorter = (a, b) =>
            (a[col.dataIndex as string] || "").localeCompare(
              b[col.dataIndex as string] || "",
            );
        }
      }

      if (!baseColumn?.width) baseColumn.width = "100px";

      if (!baseColumn?.render) {
        baseColumn.render = (v, r) => {
          const data = _.get(r, col.dataIndex as string);
          return typeof data === "string" ? (
            <Typography.Text ellipsis>{data || " - "}</Typography.Text>
          ) : (
            v || " - "
          );
        };
      }

      return baseColumn;
    })
    .filter((col) => !col.hidden);

  return filteredColumnsConfig;
};

export const toggleColumnOrder = (sortOrder?: "ASC" | "DESC") => {
  switch (sortOrder) {
    case "ASC":
      return "ascend";

    case "DESC":
      return "descend";

    default:
      return undefined;
  }
};

export const createYupSync = ({
  schema,
  formData,
}: {
  schema: yup.ObjectSchema<any>;
  formData?: Record<string, any>;
  messages?: Record<string, string> | Record<string, MessageFormatElement[]>;
}): RuleObject => ({
  async validator({ field }: any, value: unknown) {
    const fieldKeys = field?.split(".") || [];
    const obj: Record<string, any> = {};
    let temp = obj;

    fieldKeys.forEach((key, index) => {
      if (index === fieldKeys.length - 1) {
        temp[key] = value;
      } else {
        temp[key] = {};
      }
      temp = temp[key];
    });

    try {
      await schema.validateSyncAt(field, { ...obj, ...formData });
    } catch (e) {
      throw new Error((e as yup.ValidationError).message);
    }
  },
});
